import React, { type ReactElement, type ComponentProps, useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { ChevronDown } from "../icons";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { useFocusRing } from "../util/useFocusRing";

export interface SelectProps {
  /**
   * When the button is pressed
   */
  onPress: () => void;
  /**
   * Disables the control.
   */
  disabled: boolean;
  /**
   * Styles the input to indicate that it is invalid.
   */
  invalid: boolean;
  /**
   * Label to show in the button for the currently selected element
   */
  label?: string;
  /**
   * Placeholder to show when no element is selected
   */
  placeholder?: string;
  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

/**
 * Implements a button to display for the Select component
 */
export function SelectButton({
  ref,
  onPress,
  invalid,
  disabled,
  label,
  placeholder,
  sx,
  ...a11yProps
}: SelectProps & ComponentProps<typeof Pressable>): ReactElement {
  const theme = useTheme();
  const { isFocusVisible, focusProps } = useFocusRing();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        base: {
          flexDirection: "row",
          zIndex: 0,
          backgroundColor: theme.colors.background,
          borderRadius: theme.borderRadius.md,
          borderWidth: 1,
        },
        buttonContainer: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginVertical: theme.spacing.s3,
          marginHorizontal: theme.spacing.s3,
          flex: 1,
        },
        buttonStyles: {
          zIndex: 1,
          borderWidth: 0,
        },
      }),
    [theme],
  );

  return (
    // accessibilityRole and other SelectInput props are set via a11yProps for ARIA flexibility
    // eslint-disable-next-line @indeed/indeed-a11y-react-native/has-accessibility-role
    <Pressable
      ref={ref}
      testID="select-button"
      style={[
        styles.base,
        {
          borderColor: invalid ? theme.colors.foregroundStatusCritical : theme.colors.borderAction,
        },
        isFocusVisible && {
          outlineStyle: "solid",
          outlineWidth: 2,
          outlineColor: theme.colors.utilityFocus,
          outlineOffset: 2,
        },
        sx,
      ]}
      onPress={onPress}
      disabled={disabled}
      {...a11yProps}
      onFocus={focusProps.onFocus}
      onBlur={focusProps.onBlur}
    >
      <View aria-invalid={invalid} style={styles.buttonContainer}>
        <Text
          level={3}
          sx={{
            color: label ? theme.colors.foregroundStrong : theme.colors.foregroundWeak,
          }}
        >
          {label || placeholder}
        </Text>
        <ChevronDown aria-hidden={true} color={theme.colors.foregroundStrong} />
      </View>
    </Pressable>
  );
}
